<template>
	<EventStageLayout :event="event" :subtitle="trans('events.stages.survey.title')" @store="store">
		<Empty v-if="stages.length === 0"/>
		<CardGrid v-else tight>
			<template v-for="stage in stages" :key="`survey-stage-${stage.id}`">
				<Modal :id="`edit-survey-stage-${stage.id}-modal`" overflow>
					<template #title>{{ trans('events.stages.survey.edit') }}</template>
					<EditStage :stage="stage" morpher="survey" @update="update" @remove="remove">
						<LabeledObject>
							<template #label>{{ trans('events.stages.survey.required_for_certificate') }}</template>
							<Switch v-model="stage.required_for_certificate" label="events.stages.survey.required_for_certificate" full/>
						</LabeledObject>
					</EditStage>
					<template #footer>
						<PrimaryButton :color="Color.Green" @click.stop="update(stage)">
							<i class="fa-solid fa-check"/>
						</PrimaryButton>
					</template>
				</Modal>
				<Card :title="stage.title">
					<template #badges>
						<Badge v-if="stage.active" :color="Color.Green" active>{{ trans('events.stages.active') }}</Badge>
						<Badge v-else :color="Color.Red">{{ trans('events.stages.inactive') }}</Badge>
						<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(stage.id.toString())">#{{ stage.id }}</Badge>
					</template>
					<LabeledObject>
						<template #label>{{ trans('events.stages.begins_at') }}</template>
						<p>{{ dayjs(stage.begins_at).format(trans('general.dayjs.datetime')) }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.stages.ends_at') }}</template>
						<p>{{ dayjs(stage.ends_at).format(trans('general.dayjs.datetime')) }}</p>
					</LabeledObject>
					<template #footer>
						<Link :as="SecondaryButton" :color="Color.Blue" :href="route('events.stages.survey.edit', {event: event.id, stage: stage.id})">
							<i class="fa-solid fa-edit mr-2"/>
							{{ trans('general.edit') }}
						</Link>
						<SoftButton :color="Color.Red" @click.stop="remove(stage)">
							<i class="fa-solid fa-trash mr-2"/>
							{{ trans('general.delete') }}
						</SoftButton>
					</template>
				</Card>
			</template>
		</CardGrid>
	</EventStageLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import type {Event} from '@/Types/Events/Event'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import {type InertiaForm, Link} from '@inertiajs/vue3'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import EventStageLayout from '@/Layouts/EventStageLayout.vue'
import EditStage from '@/Components/Events/Stages/EditStage.vue'
import {type SurveyStage} from '@/Types/Events/Stages/SurveyStage'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {copyTextToClipboard, showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event: Event,
	selectedTab: string,
	stages: Array<SurveyStage>,
}>()

const event:Ref<Event> = ref(props.event)
const working: Ref<boolean> = ref(false)
const stages: Ref<Array<SurveyStage>> = ref(props.stages)

function store(form:InertiaForm<SurveyStage>):void{
	if(!working.value){
		form.post(route('events.stages.survey.store', event.value.id), {
			onStart: () => {
				working.value = true
				showInfo(trans('general.inserting'))
			},
			onSuccess: () => {
				showSuccess(trans('general.inserted'))
			},
			onFinish: () => {
				working.value = false
			},
			onError: showValidationErrors,
		})
	}
}

function update(stage: SurveyStage): void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.survey.update', {event: event.value.id, stage: stage.id}), stage).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function remove(stage: SurveyStage): void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.stages.survey.delete', stage.id)).then(() => {
			showSuccess(trans('general.deleted'))
			const index = stages.value.findIndex((s: SurveyStage) => s.id === stage.id)
			if(index > -1){
				stages.value.splice(index, 1)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>
